import { DeviceStatus } from '@/api/deviceApi'
import { isUnderBasePath } from '@/data/nav-data'
import { type OrgSegment, type TenantSegment, orgPath, projectPath } from '@/lib/routeScope'

/** A route pattern with its parameters filled in, for comparing a link target against the current URL. */
export const resolveActualUrl = (routeInfo: { to: string; params: Record<string, string> | undefined }) => {
	let actualUrl = routeInfo.to
	if (routeInfo.params) {
		for (const [key, value] of Object.entries(routeInfo.params)) {
			actualUrl = actualUrl.replace(`$${key}`, value)
		}
	}
	return actualUrl
}

/** Ids a nav path is resolved against. `routeTenantId` is absent on organisation URLs, where `tenantId`
 * may still hold a fallback from auth state, so the two are not interchangeable. */
export interface NavRouteScope {
	orgId: OrgSegment | undefined
	tenantId: TenantSegment | undefined
	routeTenantId: TenantSegment | undefined
}

export type NavRouteTarget = { to: string; params: Record<string, string> | undefined }

/**
 * Every tabbed module, in the order they are matched. A nav path names the module and, when it goes a
 * segment deeper, the tab; the route templates say which parameter that tab fills. A module reachable at
 * both levels carries both templates, and `project` wins whenever the URL is actually project scoped.
 *
 * A template without a `$` parameter takes no tab, which is how organisation audit logs stay tabless.
 */
interface NavModule {
	base: string
	param: string
	defaultTab: string
	/** Route used when the URL is project scoped. */
	project?: string
	/** Route used when the URL is organisation scoped. */
	org?: string
	/** Tabs the module recognises. Anything else falls back to `defaultTab` rather than becoming a URL. */
	tabs?: readonly string[]
	/**
	 * Settings modules follow the tenant in the URL, so an organisation settings link cannot be dragged
	 * into a project by the fallback tenant that auth state still holds. Everything else uses that
	 * fallback, which is what lets a project link work from an organisation page.
	 */
	followsRouteTenant?: boolean
}

const NAV_MODULES: readonly NavModule[] = [
	{ base: '/inventory', param: 'status', defaultTab: DeviceStatus.All, org: '/orgs/$orgId/inventory/$status' },
	{ base: '/meters', param: 'module', defaultTab: 'devices', org: '/orgs/$orgId/meters/$module' },
	{ base: '/operations', param: 'type', defaultTab: 'all', org: '/orgs/$orgId/operations/$type' },
	{ base: '/billing', param: 'type', defaultTab: 'plan-details', org: '/orgs/$orgId/billing/$type' },
	{
		base: '/settings/audit-logs',
		param: 'type',
		defaultTab: 'all',
		project: '/orgs/$orgId/projects/$tenantId/settings/audit-logs/$type',
		// At organisation level the tab is `all` or a project id, since the page lists one tab per project.
		org: '/orgs/$orgId/settings/audit-logs/$type',
		followsRouteTenant: true,
	},
	{
		base: '/settings/users',
		param: 'type',
		defaultTab: 'all',
		project: '/orgs/$orgId/projects/$tenantId/settings/users/$type',
		followsRouteTenant: true,
	},
	{
		base: '/settings/roles',
		param: 'type',
		defaultTab: 'all',
		project: '/orgs/$orgId/projects/$tenantId/settings/roles/$type',
		followsRouteTenant: true,
	},
	{
		base: '/settings/api-keys',
		param: 'type',
		defaultTab: 'all',
		project: '/orgs/$orgId/projects/$tenantId/settings/api-keys/$type',
		org: '/orgs/$orgId/settings/api-keys/$type',
		followsRouteTenant: true,
	},
	{
		base: '/device-management',
		param: 'status',
		defaultTab: DeviceStatus.Active,
		project: '/orgs/$orgId/projects/$tenantId/device-management/$status',
	},
	{
		base: '/data/dashboards',
		param: 'type',
		defaultTab: 'all',
		project: '/orgs/$orgId/projects/$tenantId/data/dashboards/$type',
	},
	{
		base: '/sessions/rules',
		param: 'type',
		defaultTab: 'all',
		project: '/orgs/$orgId/projects/$tenantId/sessions/rules/$type',
	},
	{
		base: '/sessions/dashboard',
		param: 'type',
		defaultTab: 'all',
		project: '/orgs/$orgId/projects/$tenantId/sessions/dashboard/$type',
	},
	{
		base: '/data/dbc-parsers',
		param: 'type',
		defaultTab: 'all',
		project: '/orgs/$orgId/projects/$tenantId/data/dbc-parsers/$type',
	},
	{
		base: '/actions/assets',
		param: 'type',
		defaultTab: 'files',
		tabs: ['files', 'firmware', 'json', 'geofence'],
		project: '/orgs/$orgId/projects/$tenantId/actions/assets/$type',
	},
]

/** The tab a nav path asks for. A path that stops at the module names no tab. */
function resolveTab(itemPath: string, module: NavModule): string {
	const isBareModule = itemPath === module.base
	const lastSegment = itemPath.split('/').pop()
	const tab = isBareModule || !lastSegment ? module.defaultTab : lastSegment
	return module.tabs && !module.tabs.includes(tab) ? module.defaultTab : tab
}

/** A URL built by hand, for a path no module claims. */
function literalTarget(itemPath: string, { orgId, tenantId, routeTenantId }: NavRouteScope): NavRouteTarget {
	if (!orgId) {
		return { to: itemPath, params: undefined }
	}
	if (itemPath.startsWith('/settings')) {
		const to = routeTenantId ? projectPath(orgId, routeTenantId, itemPath) : orgPath(orgId, itemPath)
		return { to, params: undefined }
	}
	return tenantId
		? { to: projectPath(orgId, tenantId, itemPath), params: undefined }
		: { to: itemPath, params: undefined }
}

/**
 * The router target a sidebar link points at. Nav paths are stored without ids, so each module says which
 * route it belongs to and which segment of its own path fills that route's parameter.
 *
 * Pure, and separated from the component only so it can be tested: it was a closure over the ids below.
 */
export function getNavRouteTarget(itemPath: string, scope: NavRouteScope): NavRouteTarget {
	const { orgId, tenantId, routeTenantId } = scope
	const module = NAV_MODULES.find(entry => isUnderBasePath(itemPath, entry.base))
	if (!module || !orgId) {
		return literalTarget(itemPath, scope)
	}

	const projectTenantId = module.followsRouteTenant ? routeTenantId : tenantId
	const to = projectTenantId && module.project ? module.project : module.org
	if (!to) {
		return literalTarget(itemPath, scope)
	}

	const params: Record<string, string> = { orgId }
	if (to.includes('$tenantId') && projectTenantId) {
		params.tenantId = projectTenantId
	}
	if (to.includes(`$${module.param}`)) {
		params[module.param] = resolveTab(itemPath, module)
	}
	return { to, params }
}
